import React, { useState } from "react";
import { Tab } from "@headlessui/react";
import { useTranslation } from "react-i18next";

/**
 * Always use the *English* MBTI keys for dynamic value lookup,
 * but allow trait labels to be translated.
 */
const DEFAULT_TRAIT_PAIRS = [
  ["INTROVERT", "EXTROVERT"],
  ["SENSING", "INTUITION"],
  ["THINKING", "FEELING"],
  ["JUDGING", "PROSPECTING"],
];

const TabSection = ({ item, result }) => {
  const { t } = useTranslation("result");
  const getValues = (data1, data2) => {
    let colorClass, justify, largerPercentage;
    // Parse percentage values for proper comparison
    const value1 = parseFloat(data1) || 0;
    const value2 = parseFloat(data2) || 0;
    largerPercentage = value1 > value2 ? data1 : data2;
    if (largerPercentage === data1) {
      justify = "justify-start";
      colorClass = "bg-green-500";
    } else if (largerPercentage === data2) {
      justify = "justify-end";
      colorClass = "bg-green-500";
    }
    return { colorClass, justify, largerPercentage };
  };
  const [activeTab, setActiveTab] = useState(0);
  const totalTabs = 4;

  // Get translated trait labels
  const traitPairsTranslated = t("pdfResultCard.traits", { returnObjects: true });
  const mainTitles = t("tabSection.mainTitles", { returnObjects: true });
  const descriptions = t("tabSection.descriptions", { returnObjects: true });
  
  // Get trait labels - fallback to English keys if translation missing
  const getTraitLabel = (traitKey, index, position) => {
    if (Array.isArray(traitPairsTranslated) && Array.isArray(traitPairsTranslated[index])) {
      return traitPairsTranslated[index][position];
    }
    // Fallback to English label from translation key
    const fallbackLabels = t("tabSection.traitLabels", { returnObjects: true });
    if (fallbackLabels && fallbackLabels[traitKey]) {
      return fallbackLabels[traitKey];
    }
    // Final fallback to key itself
    return traitKey;
  };

  // Build transformed data using English keys for values but translated labels
  const transformedData = DEFAULT_TRAIT_PAIRS.map(([leftTrait, rightTrait], index) => {
    const leftValue = result?.prediction?.mbti?.values?.[leftTrait] || "0.0%";
    const rightValue = result?.prediction?.mbti?.values?.[rightTrait] || "0.0%";
    const mainTitle = Array.isArray(mainTitles) ? mainTitles[index] : mainTitles;
    const detail = Array.isArray(descriptions) ? descriptions[index] : "";

    return [
      {
        key: getTraitLabel(leftTrait, index, 0),
        value: leftValue,
        main_title: mainTitle,
        detail: detail,
      },
      {
        key: getTraitLabel(rightTrait, index, 1),
        value: rightValue,
        main_title: mainTitle,
        detail: detail,
      },
    ];
  });
  return (
    <div>
      <Tab.Group onChange={(index) => setActiveTab(index)}>
        <div className="flex  rounded-2xl bg-white overflow-hidden sm-max:hidden">
          <Tab.List className="flex  flex-col w-2/5 bg-[#EDEDED]">
            <div className="bg-white w-full flex flex-col">
              {transformedData?.map((item, index) => (
                <Tab
                  key={index}
                  className={`py-[1.5rem] px-[1rem] outline-none ${index === activeTab
                    ? "flex bg-white rounded-bl-2xl rounded-tl-2xl "
                    : index === (activeTab - 1 + totalTabs) % totalTabs && index < totalTabs - 1
                      ? "bg-[#EDEDED] rounded-br-2xl "
                      : activeTab === totalTabs - 1 && index < totalTabs - 1
                        ? "bg-[#EDEDED] "
                        : index === (activeTab + 1) % totalTabs
                          ? "bg-[#EDEDED] rounded-tr-2xl "
                          : "bg-[#EDEDED]"
                    } `}
                >
                  <div className="w-full">
                    <div
                      className={`w-full flex ${getValues(item[0].value, item[1].value).justify
                        } bg-[#D9D9D9] rounded-full`}
                    >
                      <div
                        className={`rounded-full h-[10px] ${getValues(item[0].value, item[1].value).colorClass
                          }`}
                        style={{
                          width: `${getValues(item[0].value, item[1].value)
                            .largerPercentage
                            }`,
                        }}
                      ></div>
                    </div>

                    <div className="flex justify-between">
                      <div>
                        <p>{parseInt(item[0].value?.split('.')[0])}%</p>
                        {item[0].key}
                      </div>
                      <div>
                        <p className="text-right">{parseInt(item[1].value?.split('.')[0])}%</p>
                        <p> {item[1].key}</p>
                      </div>
                    </div>
                  </div>
                </Tab>
              ))}
            </div>
          </Tab.List>
          <Tab.Panels className="w-3/5">
            {transformedData?.map((item) => {
              let largerPercentage, largerPercentage_key, main_title;
              // Parse percentage values for proper comparison
              const value1 = parseFloat(item[0].value) || 0;
              const value2 = parseFloat(item[1].value) || 0;
              largerPercentage =
                value1 > value2 ? item[0].value : item[1].value;
              largerPercentage_key =
                value1 > value2 ? item[0].key : item[1].key;
              main_title =
                value1 > value2 ? item[0].main_title : item[1].main_title;
              return (
                <Tab.Panel>
                  <div className="flex flex-col justify-center items-center px-[2rem] py-9 gap-[2rem] md-max:gap-5">
                    <p className="text-[#3976f9] text-[3rem] font-semibold">
                      {main_title}
                    </p>
                    <div className="flex flex-col items-center">
                      <p className="text-[2rem] font-semibold">
                        {parseInt(largerPercentage?.split('.')[0])}%
                      </p>
                      <p className="text-2xl font-semibold">
                        {largerPercentage_key}
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 justify-center items-center">
                      <p className="text-center w-9/12">
                        {transformedData[activeTab][0]?.detail}
                      </p>
                      {/* <p className="text-center">
                        Dictum vitae non in elementum elit egestas nisl lorem
                        facilisi.
                      </p> */}
                    </div>
                  </div>
                </Tab.Panel>
              );
            })}
          </Tab.Panels>
        </div>
      </Tab.Group>
    </div>
  );
};

export default TabSection;







